//
// DISCLAIMER
//
// Copyright 2020 ArangoDB GmbH, Cologne, Germany
//
// Author Ewout Prangsma
//

import React, { Component } from "react";
import { RouteComponentProps } from "react-router-dom";
import { Button, Modal } from "semantic-ui-react";
import apiClients from "../../api/apiclients";
import { ContentActionButton, ErrorMessage, Processing } from "../../ui/lib";
import { IWithRefreshProps, withRefresh } from "../../util/WithRefresh";
import { CreateDebugPackageRequest as ApiCreateDebugPackageRequest } from "../../api/lib";

interface IDebugPackageModalViewArgs {
  // args
  deploymentId: string;
  jobId?: string;

  // state
  processing: boolean;
  open: boolean;
  loading: boolean;
  errorMessage?: string;

  // actions
  onDebugPackage: () => void;
  onClose: () => void;
}

const DebugPackageModalView = ({ ...args }: IDebugPackageModalViewArgs) => {
  const has_jobId = !!args.jobId;

  return (
    <Modal open={args.open} onClose={args.onClose}>
      <Modal.Header>
        <h1>Debug package</h1>
      </Modal.Header>
      <Modal.Content>
        <Processing active={args.processing} message="Starting debug package creation..." />
        <ErrorMessage message={args.errorMessage} active={!!args.errorMessage} />
        {!has_jobId && (
          <div>
            <p>
              Click <strong>Create</strong> to start building the debug package. This can take up to an hour.
            </p>
            <p>
              This package will be available for download in the{" "}
              <strong>
                <a href="#jobs">jobs</a>
              </strong>{" "}
              tab.
            </p>
          </div>
        )}
        {has_jobId && (
          <div>
            <p>
              The debug package is being built. To download it, go to the{" "}
              <strong>
                <a href="#jobs">jobs</a>
              </strong>{" "}
              tab and wait for the job with id {args.jobId} to finish.
            </p>
          </div>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button primary onClick={args.onDebugPackage} content="Create" disabled={args.processing || has_jobId} />
        <Button onClick={args.onClose} content={has_jobId ? "Close" : "Cancel"} />
      </Modal.Actions>
    </Modal>
  );
};

interface IDebugPackageModalProps extends IWithRefreshProps, RouteComponentProps {
  deploymentId: string;
}

interface IDebugPackageModalState {
  open: boolean;
  processing: boolean;
  errorMessage?: string;
  jobId?: string;
}

// Component to launch a debug container
class DebugPackageModal extends Component<IDebugPackageModalProps, IDebugPackageModalState> {
  state: IDebugPackageModalState = {
    open: false,
    processing: false,
    errorMessage: undefined,
    jobId: undefined,
  };

  onDebugPackage = async () => {
    this.setState({ processing: true, errorMessage: undefined });
    try {
      const req: ApiCreateDebugPackageRequest = {
        deployment_id: this.props.deploymentId,
      };
      const resp = await apiClients.idashboardClient.CreateDebugPackage(req);
      this.setState({ processing: false, jobId: resp.job_id });
    } catch (e) {
      this.setState({ processing: false, errorMessage: e });
    }
  };

  onOpen = () => {
    this.setState({ open: true, jobId: undefined });
  };

  onClose = () => {
    this.setState({ open: false });
  };

  render() {
    return (
      <span>
        <ContentActionButton primary onClick={this.onOpen} content="Debug Package" icon="bug" />
        <DebugPackageModalView {...this.props} {...this.state} {...this} />
      </span>
    );
  }
}

export default withRefresh()(DebugPackageModal);
